import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import CampaignInfo from './wizard/CampaignInfo';
import LeadSelection from './wizard/LeadSelection';
import SequenceBuilder from './wizard/SequenceBuilder';
import ScheduleCampaign from './wizard/ScheduleCampaign';
import ReviewCampaign from './wizard/ReviewCampaign';
import '../../styles/CampaignWizard.css';

// Import icons
import { 
  FiInfo, 
  FiUsers, 
  FiMail, 
  FiCalendar, 
  FiCheckCircle,
  FiArrowLeft,
  FiArrowRight,
  FiSave
} from 'react-icons/fi';

const CampaignWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  
  // Campaign state
  const [campaign, setCampaign] = useState({
    name: '',
    description: '',
    status: 'Draft',
    leads: [],
    sequences: [{ 
      subject: '', 
      content: '', 
      templateId: '', 
      order: 1,
      delayDays: 0,
      delayHours: 0
    }],
    schedule: {
      startDate: new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0],
      endDate: '',
      sendingHours: {
        from: '09:00',
        to: '17:00',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      sendingDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
    },
    analytics: {
      sent: 0,
      opened: 0,
      clicked: 0,
      replied: 0,
      opportunities: 0
    }
  });
  
  // Fetch email templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await api.get('/templates');
        setTemplates(response.data);
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };
    
    fetchTemplates();
  }, []);
  
  // Step configuration
  const steps = [
    {
      id: 1,
      title: 'Campaign Info',
      description: 'Set your campaign name and description',
      icon: <FiInfo />
    },
    {
      id: 2,
      title: 'Select Leads',
      description: 'Choose leads for your campaign',
      icon: <FiUsers />
    },
    {
      id: 3,
      title: 'Email Sequence',
      description: 'Create your email sequence',
      icon: <FiMail />
    },
    {
      id: 4,
      title: 'Schedule',
      description: 'Set when your emails will be sent',
      icon: <FiCalendar />
    },
    {
      id: 5,
      title: 'Review',
      description: 'Review and launch your campaign',
      icon: <FiCheckCircle />
    }
  ];
  
  // Handle campaign data changes
  const handleCampaignChange = (field, value) => {
    setCampaign(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle sequence changes
  const handleSequenceChange = (sequences) => {
    setCampaign(prev => ({
      ...prev,
      sequences
    }));
  };
  
  // Handle schedule changes
  const handleScheduleChange = (scheduleData) => {
    setCampaign(prev => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        ...scheduleData
      }
    }));
  };
  
  // Next step handler
  const handleNextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Previous step handler
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  
  // Go to specific step
  const goToStep = (step) => {
    if (step >= 1 && step <= currentStep) {
      setCurrentStep(step);
      window.scrollTo(0, 0);
    }
  };
  
  // Save campaign
  const saveCampaign = async (status = 'Draft') => {
    try {
      setLoading(true);
      
      // Update campaign status if provided
      const campaignData = {
        ...campaign,
        status
      };
      
      // Format sequences for API
      campaignData.sequences = campaignData.sequences.map((seq, index) => ({
        ...seq,
        order: index + 1
      }));
      
      const response = await api.post('/campaigns', campaignData);
      
      if (response.data) {
        // Navigate to campaign list or detail page
        navigate('/campaigns');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      // Show error notification
    } finally {
      setLoading(false);
    }
  };
  
  // Launch campaign
  const launchCampaign = async () => {
    await saveCampaign('Active');
  };
  
  // Step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CampaignInfo 
            campaign={campaign} 
            onChange={handleCampaignChange} 
          />
        );
      case 2:
        return (
          <LeadSelection 
            selectedLeads={campaign.leads} 
            onChange={(leads) => handleCampaignChange('leads', leads)} 
          />
        );
      case 3:
        return (
          <SequenceBuilder 
            sequences={campaign.sequences} 
            templates={templates}
            onChange={handleSequenceChange} 
          />
        );
      case 4:
        return (
          <ScheduleCampaign 
            schedule={campaign.schedule} 
            onChange={handleScheduleChange} 
          />
        );
      case 5:
        return (
          <ReviewCampaign 
            campaign={campaign} 
            templates={templates}
          />
        );
      default:
        return null;
    }
  };
  
  return (
    <div className="campaign-wizard-container">
      <div className="wizard-header">
        <h1>Create New Campaign</h1>
      </div>
      
      <div className="wizard-progress">
        {steps.map((step) => (
          <div 
            key={step.id} 
            className={`wizard-step ${currentStep === step.id ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
            onClick={() => goToStep(step.id)}
          >
            <div className="step-icon">
              {currentStep > step.id ? <FiCheckCircle /> : step.icon}
            </div>
            <div className="step-content">
              <div className="step-title">{step.title}</div>
              <div className="step-description">{step.description}</div>
            </div>
            {step.id < steps.length && (
              <div className="step-connector"></div>
            )}
          </div>
        ))}
      </div>
      
      <div className="wizard-content">
        {renderStepContent()}
      </div>
      
      <div className="wizard-footer">
        <div className="wizard-buttons">
          {currentStep > 1 && (
            <button 
              className="btn btn-secondary" 
              onClick={handlePrevStep}
              disabled={loading}
            >
              <FiArrowLeft className="btn-icon" />
              Previous
            </button>
          )}
          
          {currentStep < steps.length ? (
            <button 
              className="btn btn-primary" 
              onClick={handleNextStep}
              disabled={loading}
            >
              Next
              <FiArrowRight className="btn-icon-right" />
            </button>
          ) : (
            <div className="final-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => saveCampaign('Draft')}
                disabled={loading}
              >
                <FiSave className="btn-icon" />
                Save as Draft
              </button>
              
              <button 
                className="btn btn-primary" 
                onClick={launchCampaign}
                disabled={loading}
              >
                <FiCheckCircle className="btn-icon" />
                Launch Campaign
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignWizard;